// PR A (fix/contracts-installation-context-v2): pure, testable helpers for
// installation identity, navigation and economic-mode semantics (mandate §4).
//
// These functions are side-effect free so they can be unit-tested without a
// database. The UI components consume them; the server enrichment already
// exposes siteName / contractCode / contractTitle / projectManagerName.

export interface InstallationIdentityLike {
  id: number;
  installationId?: number;
  name?: string | null;
  workDescription?: string | null;
  siteName?: string | null;
  siteId?: number | null;
  siteAddressLine1?: string | null;
  siteAddressLine2?: string | null;
  sitePostalCode?: string | null;
  siteCity?: string | null;
  siteCountryCode?: string | null;
  installationCode?: string | null;
  ticketReference?: string | null;
  clientName?: string | null;
  clientId?: string | null;
  address?: string | null;
  contractId?: number | null;
  contractCode?: string | null;
  contractTitle?: string | null;
  contractCustomerPartnerId?: number | null;
  canonicalCustomerPartnerId?: number | null;
  canonicalCustomerName?: string | null;
  canonicalCustomerCode?: string | null;
  projectManagerName?: string | null;
  installationPrice?: string | number | null;
  workOrderType?: string | null;
  status?: string | null;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}

export function sanitizeInstallationString(val: string | null | undefined): string | null {
  if (val == null) return null;
  const trimmed = String(val).trim();
  if (!trimmed || ["null", "undefined", "nan"].includes(trimmed.toLowerCase())) return null;
  return trimmed;
}

// Returns a display string for the primary code of a contract-installation row,
// using the fallback chain: installationCode → clientId/expedienteCode → "Sin código".
// Sanitizes literal sentinel strings (null, undefined, NaN) to avoid rendering them.
export function resolveInstallationCodeLabel(inst: {
  installationCode?: string | number | null;
  clientId?: string | number | null;
  expedienteCode?: string | number | null;
}): string {
  const sanitizeCandidate = (value: string | number | null | undefined): string | null => {
    if (value == null) return null;
    const candidate = String(value).trim();
    if (!candidate || ["null", "undefined", "nan"].includes(candidate.toLowerCase())) return null;
    return candidate;
  };

  for (const candidate of [inst.installationCode, inst.clientId, inst.expedienteCode]) {
    const sanitized = sanitizeCandidate(candidate);
    if (sanitized) return sanitized;
  }

  return "Sin código";
}

// Presentation limit only: existing data and searchable descriptions are preserved.
export const INSTALLATION_SHORT_NAME_MAX_LENGTH = 80;

// Only short, single-line legacy labels can stand in for a missing name.
// A technical paragraph must never become a heading (INST-06).
export function resolveInstallationTitle(inst: InstallationIdentityLike): string {
  const canonicalName = sanitizeInstallationString(inst.name);
  if (canonicalName) {
    const singleLine = canonicalName.replace(/\s+/g, " ");
    return singleLine.length <= INSTALLATION_SHORT_NAME_MAX_LENGTH
      ? singleLine
      : `${singleLine.slice(0, INSTALLATION_SHORT_NAME_MAX_LENGTH - 1).trimEnd()}…`;
  }
  const legacyName = sanitizeInstallationString(inst.workDescription);
  if (legacyName && legacyName.length <= INSTALLATION_SHORT_NAME_MAX_LENGTH && !/[\r\n;]|\s\|\s/.test(legacyName)) return legacyName;
  const id = inst.id ?? inst.installationId;
  return id != null ? `Instalación ${id}` : "Instalación sin identificar";
}

// Sede label used in header / list secondary line.
export function resolveSiteLabel(inst: InstallationIdentityLike): string | null {
  return sanitizeInstallationString(inst.siteName);
}

// Contract reference shown in header / list (CTR-XXXX form or raw code).
export function resolveContractLabel(inst: InstallationIdentityLike): string | null {
  const code = sanitizeInstallationString(inst.contractCode);
  if (code) return code;
  if (inst.contractId) return `CTR-${inst.contractId}`;
  return null;
}

// Client presentation helper: canonical name with fallback to legacy clientName,
// Customer 360 vs legacy provenance, and customer partner code / expediente.
export interface ClientDisplayInfo {
  name: string;
  provenance: "customer360" | "legacy";
  provenanceLabel: string;
  customerCode: string | null;
}

export function resolveClientDisplayInfo(inst: InstallationIdentityLike): ClientDisplayInfo {
  const canonical = sanitizeInstallationString(inst.canonicalCustomerName);
  const legacy = sanitizeInstallationString(inst.clientName);
  const hasPartnerId = inst.canonicalCustomerPartnerId != null && Number(inst.canonicalCustomerPartnerId) > 0;

  const name = canonical || legacy || "Cliente sin identificar";
  const provenance: "customer360" | "legacy" = (canonical && hasPartnerId) || hasPartnerId ? "customer360" : "legacy";
  const provenanceLabel = provenance === "customer360" ? "Cliente 360" : "Provenance legacy";
  const customerCode = sanitizeInstallationString(inst.canonicalCustomerCode) || sanitizeInstallationString(inst.clientId);

  return {
    name,
    provenance,
    provenanceLabel,
    customerCode,
  };
}

// Code display helper: installation code, contract label, and expediente.
export interface CodeDisplayInfo {
  installationCode: string | null;
  contractCode: string | null;
  expedienteCode: string | null;
}

export function resolveCodeDisplayInfo(inst: InstallationIdentityLike): CodeDisplayInfo {
  const installationCode = sanitizeInstallationString(inst.installationCode);
  const contractCode = resolveContractLabel(inst);
  const expedienteCode = sanitizeInstallationString(inst.clientId);

  return {
    installationCode,
    contractCode,
    expedienteCode,
  };
}

// Site and address helper for list display.
export interface SiteAndAddressDisplayInfo {
  siteName: string | null;
  address: string;
  combinedLabel: string;
  provenance: "site" | "legacy" | "missing";
  provenanceLabel: string;
  mapsUrl: string | null;
}

export function resolveSiteAndAddressDisplayInfo(inst: InstallationIdentityLike): SiteAndAddressDisplayInfo {
  const siteName = resolveSiteLabel(inst);
  const usableStreet = (value: string | null | undefined) => {
    const clean = sanitizeInstallationString(value);
    return clean && !/^(?:n\/a|sin direcci[oó]n|[-—])$/i.test(clean) ? clean : null;
  };
  const line1 = usableStreet(inst.siteAddressLine1);
  // A municipality/country alone cannot locate a work site. Never assemble a
  // legacy street with canonical locality fields or use a customer's fiscal data.
  const hasSiteAddress = Number(inst.siteId) > 0 && !!line1;
  const legacy = usableStreet(inst.address);
  const locality = [inst.sitePostalCode, inst.siteCity]
    .map(sanitizeInstallationString).filter(Boolean).join(" ");
  const address = hasSiteAddress
    ? [line1, sanitizeInstallationString(inst.siteAddressLine2), locality,
        sanitizeInstallationString(inst.siteCountryCode)]
        .filter(Boolean).join(", ")
    : legacy || "Sin dirección";
  const provenance = hasSiteAddress ? "site" : legacy ? "legacy" : "missing";
  const combinedLabel = siteName ? `${siteName} — ${address}` : address;

  return {
    siteName,
    address,
    combinedLabel,
    provenance,
    provenanceLabel: provenance === "site" ? "Dirección de sede"
      : provenance === "legacy" ? "Dirección legacy" : "Sin dirección",
    mapsUrl: provenance === "missing" ? null
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
  };
}

// Format date strings or Date objects safely sanitizing sentinels.
function formatDateString(val: string | Date | null | undefined): string | null {
  if (!val) return null;
  const str = typeof val === "string" ? val : val.toISOString();
  const clean = str.split("T")[0];
  const parts = clean.split("-");
  if (parts.length < 3 || parts.some((p) => !p || p.toLowerCase().includes("null") || p.toLowerCase().includes("undefined"))) {
    return null;
  }
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

// PM and Date information helper.
export interface PmAndDatesDisplayInfo {
  projectManagerName: string;
  startDateFormatted: string | null;
  endDateFormatted: string | null;
  dateRangeFormatted: string | null;
}

export function resolvePmAndDatesDisplayInfo(inst: InstallationIdentityLike): PmAndDatesDisplayInfo {
  const pm = sanitizeInstallationString(inst.projectManagerName) || "Sin PM";
  const startDateFormatted = formatDateString(inst.startDate);
  const endDateFormatted = formatDateString(inst.endDate);

  let dateRangeFormatted: string | null = null;
  if (startDateFormatted && endDateFormatted) {
    dateRangeFormatted = `${startDateFormatted} - ${endDateFormatted}`;
  } else if (startDateFormatted) {
    dateRangeFormatted = `Desde ${startDateFormatted}`;
  } else if (endDateFormatted) {
    dateRangeFormatted = `Hasta ${endDateFormatted}`;
  }

  return {
    projectManagerName: pm,
    startDateFormatted,
    endDateFormatted,
    dateRangeFormatted,
  };
}

// Work order type display label.
export function resolveWorkOrderTypeLabel(workOrderType: string | null | undefined): string {
  const clean = sanitizeInstallationString(workOrderType);
  if (clean === "installation") return "Instalación";
  if (clean === "breakdown") return "Avería";
  if (clean === "maintenance") return "Mantenimiento";
  return clean || "Instalación";
}

// Status badge display info.
export interface StatusDisplayInfo {
  label: string;
  variantClass: string;
}

export function resolveStatusDisplayInfo(status: string | null | undefined): StatusDisplayInfo {
  const clean = sanitizeInstallationString(status);
  switch (clean) {
    case "completed":
      return { label: "Completada", variantClass: "bg-green-500" };
    case "in_progress":
      return { label: "Iniciada", variantClass: "bg-blue-500" };
    case "cancelled":
      return { label: "Cancelada", variantClass: "bg-red-500" };
    case "pending":
    default:
      return { label: "Pendiente", variantClass: "bg-yellow-500" };
  }
}

// Economic mode: contract-managed vs legacy installation.
export type EconomicMode = "contract" | "legacy";

export function resolveEconomicMode(inst: InstallationIdentityLike): EconomicMode {
  return inst.contractId != null && Number(inst.contractId) > 0 ? "contract" : "legacy";
}

// Distinguish the meaning of a 0 € installationPrice (contract-managed vs.
// legacy unconfigured). When contract-managed we never show 0€ as the price;
// the economics are owned by the contract (mandate §4.3).
export interface EconomicSummaryDecision {
  mode: EconomicMode;
  contractId: number | null;
  showsContractManagedMessage: boolean;
  showsLegacySummary: boolean;
  // 0€ real (legacy unconfigured) vs managed by contract
  zeroIsReal: boolean;
}

export function decideEconomicSummary(inst: InstallationIdentityLike): EconomicSummaryDecision {
  const mode = resolveEconomicMode(inst);
  if (mode === "contract") {
    return {
      mode,
      contractId: Number(inst.contractId),
      showsContractManagedMessage: true,
      showsLegacySummary: false,
      zeroIsReal: false,
    };
  }
  const price = inst.installationPrice;
  const hasPrice =
    price !== null &&
    price !== undefined &&
    price !== "" &&
    Number.isFinite(Number(price)) &&
    Number(price) > 0;
  // Legacy unconfigured -> 0€ is "no configurado", not a real 0€ contract amount.
  return {
    mode,
    contractId: null,
    showsContractManagedMessage: false,
    showsLegacySummary: true,
    zeroIsReal: hasPrice,
  };
}

export function buildContractPath(params: {
  customerPartnerId: number;
  contractId: number;
  tab?: string;
}): string {
  const base = `/customers/${params.customerPartnerId}/contracts/${params.contractId}`;
  return params.tab ? `${base}?tab=${params.tab}` : base;
}

// Search inclusion semantics for the installations list (mandate §4.2):
// match across installation name, site, client (legacy & canonical 360), codes,
// contract, address, PM. Pure and case-insensitive.
export function installationMatchesSearch(
  inst: InstallationIdentityLike,
  query: string,
): boolean {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    inst.name,
    inst.workDescription,
    inst.siteName,
    inst.clientName,
    inst.clientId,
    inst.canonicalCustomerName,
    inst.canonicalCustomerCode,
    inst.installationCode,
    inst.ticketReference,
    inst.contractCode,
    inst.address,
    inst.projectManagerName,
    inst.contractId != null ? String(inst.contractId) : null,
  ]
    .filter((v): v is string => v !== null && v !== undefined && String(v).trim() !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}
